// ListingDetails.tsx
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Heart,
  Share2,
  MapPin,
  Star,
  Bed,
  Bath,
  Maximize,
  Wifi,
  Wind,
  Tv,
  Car,
  Coffee,
  Home,
  Building2,
  Clock,
  Calendar,
  User,
  Phone,
  Mail,
  MessageCircle,
  CheckCircle,
  XCircle,
  AlertCircle,
  Navigation,
  ExternalLink,
  Copy,
  Check,
  ChevronLeft,
  ChevronRight,
  Users,
  Dumbbell,
  Waves,
  Dog,
  Shield,
  Zap,
  Droplet,
  Eye,
  ArrowUp,
  Sofa,
  BookOpen,
  Umbrella,
  Leaf,
  DoorOpen,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix Leaflet default icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Types
interface ListingDetails {
  id: string;
  product_id: string;
  title: string;
  description: string;
  price: number;
  priceUnit: "month" | "night" | "hour" | "day" | "total";
  location: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  island: string;
  type: "residential" | "commercial" | "short-stay";
  bedrooms?: number;
  bathrooms?: number;
  area?: number;
  amenities: string[];
  status: "available" | "rented" | "maintenance" | "coming_soon" | "sold";
  latitude: number;
  longitude: number;
  images: string[];
  rating?: number;
  reviews?: number;
  isNew: boolean;
  isFeatured: boolean;
  viewingCount: number;
  favoriteCount: number;
  furnished?: "unfurnished" | "semi-furnished" | "fully-furnished";
  parkingSpots: number;
  petFriendly: boolean;
  immediateAvailable: boolean;
  utilitiesIncluded: boolean;
  minimumLeaseMonths?: number;
  maximumLeaseMonths?: number;
  depositAmount?: number;
  maintenanceFee?: number;
  contact: {
    name: string;
    phone: string;
    email: string;
    avatar?: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface ListingDetailsProps {
  listing: ListingDetails;
  onClose: () => void;
  onLike?: (listing: ListingDetails) => void;
  onContact?: (listing: ListingDetails) => void;
}

// Amenity icons mapping
const amenityIcons: Record<string, any> = {
  "Air Conditioning": Wind, AC: Wind,
  "WiFi": Wifi, Wifi: Wifi,
  "TV": Tv,
  "Parking": Car,
  "Kitchen": Home,
  "Washing Machine": Home,
  "Dryer": Home,
  "Gym": Dumbbell,
  "Pool": Waves,
  "Hot Water": Droplet,
  "Elevator": ArrowUp,
  "Security": Shield,
  "Balcony": Home,
  "Garden": Leaf,
  "Beach Access": Umbrella,
  "Furnished": Sofa,
  "Pets Allowed": Dog,
  "Smoking Allowed": Coffee,
  "Wheelchair Access": Users,
  "Study Desk": BookOpen,
  "Ocean View": Eye,
  "City View": Eye,
  "Private Entrance": DoorOpen,
  "Backup Generator": Zap,
  "Water Tank": Droplet,
};

// Image Gallery Component
const ImageGallery = ({ images, title }: { images: string[]; title: string }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images?.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images?.length) % images?.length);
  };

  if (!images?.length) {
    return (
      <div className="h-96 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-2xl flex items-center justify-center">
        <Home className="w-20 h-20 text-gray-400" />
      </div>
    );
  }

  return (
    <>
      <div className="relative h-96 rounded-2xl overflow-hidden group">
        <img
          src={images[currentIndex]}
          alt={`${title} - Image ${currentIndex + 1}`}
          className="w-full h-full object-cover cursor-pointer"
          onClick={() => setIsFullscreen(true)}
        />
        
        {/* Navigation Buttons */}
        {images?.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-all opacity-0 group-hover:opacity-100"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-all opacity-0 group-hover:opacity-100"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
        
        {/* Image Counter */}
        <div className="absolute bottom-4 right-4 px-2 py-1 bg-black/50 rounded-lg text-white text-sm">
          {currentIndex + 1} / {images?.length}
        </div>
      </div>

      {/* Fullscreen Modal */}
      <AnimatePresence>
        {isFullscreen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={() => setIsFullscreen(false)}
          >
            <button
              onClick={() => setIsFullscreen(false)}
              className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
            >
              <XCircle className="w-6 h-6" />
            </button>
            <img
              src={images[currentIndex]}
              alt={title}
              className="max-w-[90vw] max-h-[90vh] object-contain"
            />
            {images?.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Map Component
const ListingMap = ({ lat, lng, title }: { lat: number; lng: number; title: string }) => {
  const position: [number, number] = [lat, lng];
  
  return (
    <div className="h-64 rounded-xl overflow-hidden">
      <MapContainer
        center={position}
        zoom={15}
        className="h-full w-full"
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={position}>
          <Popup>{title}</Popup>
        </Marker>
      </MapContainer>
    </div>
  );
};

// Amenities Grid Component
const AmenitiesGrid = ({ amenities }: { amenities: string[] }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {amenities?.map((amenity) => {
        const Icon = amenityIcons[amenity] || CheckCircle;
        return (
          <div key={amenity} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <Icon className="w-4 h-4 text-purple-500" />
            <span className="text-sm text-gray-700 dark:text-gray-300">{amenity}</span>
          </div>
        );
      })}
    </div>
  );
};

// Contact Card Component
const ContactCard = ({ contact, onContact }: { contact: ListingDetails['contact']; onContact?: () => void }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyPhone = () => {
    navigator.clipboard.writeText(contact?.phone);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <User className="w-5 h-5 text-purple-500" />
        Contact Agent
      </h3>
      
      <div className="flex items-center gap-4 mb-4">
        <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xl font-bold">
          {contact?.name.charAt(0)}
        </div>
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-white">{contact?.name}</h4>
          <p className="text-sm text-gray-500 dark:text-gray-400">Property Agent</p>
        </div>
      </div>

      <div className="space-y-3">
        <button
          onClick={handleCopyPhone}
          className="w-full flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow"
        >
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-purple-500" />
            <span className="text-sm text-gray-700 dark:text-gray-300">{contact?.phone}</span>
          </div>
          {copied ? (
            <Check className="w-4 h-4 text-green-500" />
          ) : (
            <Copy className="w-4 h-4 text-gray-400" />
          )}
        </button>
        
        <a
          href={`mailto:${contact?.email}`}
          className="w-full flex items-center gap-2 p-3 bg-white dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow"
        >
          <Mail className="w-4 h-4 text-purple-500" />
          <span className="text-sm text-gray-700 dark:text-gray-300">{contact?.email}</span>
        </a>
        
        <button
          onClick={onContact}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold transition-colors"
        >
          <MessageCircle className="w-4 h-4" />
          Send Message
        </button>
      </div>
    </div>
  );
};

// Main Component
export default function ListingDetails({ listing, onClose, onLike, onContact }: ListingDetailsProps) {
  const [isLiked, setIsLiked] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);

  const formatPrice = () => {
    const price = listing?.price ?? 0;
    const unitText = {
      month: "/month",
      night: "/night",
      hour: "/hour",
      day: "/day",
      total: " total",
    }[listing?.priceUnit];
    return `MVR ${price}${unitText}`;
  };

  const getStatusBadge = () => {
    const config = {
      available: { color: "bg-green-500", text: "Available", icon: CheckCircle },
      rented: { color: "bg-red-500", text: "Rented", icon: XCircle },
      maintenance: { color: "bg-orange-500", text: "Under Maintenance", icon: AlertCircle },
      coming_soon: { color: "bg-yellow-500", text: "Coming Soon", icon: Clock },
      sold: { color: "bg-gray-500", text: "Sold", icon: XCircle },
    };
    const { color, text, icon: Icon } = config[listing?.status] || config.available;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 ${color} text-white text-xs rounded-full`}>
        <Icon className="w-3 h-3" />
        {text}
      </span>
    );
  };

  const handleLike = () => {
    setIsLiked(!isLiked);
    onLike?.(listing);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: listing?.title,
          text: `Check out this ${listing?.type} property`,
          url: window.location.href,
        });
      } catch (error) {
        console.log("Share cancelled");
      }
    } else {
      await navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  const handleGetDirections = () => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${listing?.latitude},${listing?.longitude}`;
    window.open(url, "_blank");
  };

  return (
    <div
      className=" z-50 bg-white dark:bg-gray-900 overflow-y-auto"
    >
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back</span>
            </button>
            
            <div className="flex items-center gap-2">
              <button
                onClick={handleLike}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Heart
                  className={`w-5 h-5 transition-colors ${
                    isLiked ? "fill-red-500 text-red-500" : "text-gray-600 dark:text-gray-400"
                  }`}
                />
              </button>
              <button
                onClick={handleShare}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Share2 className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images and Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <ImageGallery images={listing?.images} title={listing?.title} />
            
            {/* Title and Basic Info */}
            <div>
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    {listing?.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-2 text-gray-500 dark:text-gray-400">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm">{listing?.location}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl md:text-3xl font-bold text-purple-600 dark:text-purple-400">
                    {formatPrice()}
                  </div>
                  {listing?.status === "available" && (
                    <div className="mt-2">{getStatusBadge()}</div>
                  )}
                </div>
              </div>
              
              {/* Stats Row */}
              <div className="flex flex-wrap gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                {listing?.bedrooms !== undefined && (
                  <div className="flex items-center gap-2">
                    <Bed className="w-5 h-5 text-purple-500" />
                    <div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Bedrooms</div>
                      <div className="font-semibold">{listing?.bedrooms}</div>
                    </div>
                  </div>
                )}
                {listing?.bathrooms !== undefined && (
                  <div className="flex items-center gap-2">
                    <Bath className="w-5 h-5 text-purple-500" />
                    <div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Bathrooms</div>
                      <div className="font-semibold">{listing?.bathrooms}</div>
                    </div>
                  </div>
                )}
                {listing?.area !== undefined && (
                  <div className="flex items-center gap-2">
                    <Maximize className="w-5 h-5 text-purple-500" />
                    <div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Area</div>
                      <div className="font-semibold">{listing?.area} sqft</div>
                    </div>
                  </div>
                )}
                {listing?.parkingSpots > 0 && (
                  <div className="flex items-center gap-2">
                    <Car className="w-5 h-5 text-purple-500" />
                    <div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Parking</div>
                      <div className="font-semibold">{listing?.parkingSpots} spot{listing?.parkingSpots !== 1 ? 's' : ''}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Description */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Description</h2>
              <p className={`text-gray-600 dark:text-gray-400 leading-relaxed ${!showFullDescription && 'line-clamp-4'}`}>
                {listing?.description}
              </p>
              {listing?.description?.length > 300 && (
                <button
                  onClick={() => setShowFullDescription(!showFullDescription)}
                  className="mt-2 text-purple-500 hover:text-purple-600 text-sm font-medium"
                >
                  {showFullDescription ? "Show less" : "Read more"}
                </button>
              )}
            </div>
            
            {/* Amenities */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Amenities</h2>
              <AmenitiesGrid amenities={listing?.amenities} />
            </div>
            
            {/* Location Map */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Location</h2>
              <ListingMap lat={listing?.latitude} lng={listing?.longitude} title={listing?.title} />
              <button
                onClick={handleGetDirections}
                className="mt-3 flex items-center gap-2 text-purple-500 hover:text-purple-600 text-sm font-medium"
              >
                <Navigation className="w-4 h-4" />
                Get Directions
              </button>
            </div>
          </div>
          
          {/* Right Column - Contact and Additional Info */}
          <div className="space-y-6">
            {/* Contact Card */}
            <ContactCard contact={listing?.contact} onContact={() => onContact?.(listing)} />
            
            {/* Additional Information */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Additional Information</h3>
              <div className="space-y-3">
                {listing?.furnished && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Furnishing</span>
                    <span className="text-sm font-medium capitalize">{listing?.furnished.replace('-', ' ')}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Property Type</span>
                  <span className="text-sm font-medium capitalize">{listing?.type}</span>
                </div>
                {listing?.petFriendly && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Pets</span>
                    <span className="text-sm font-medium text-green-600">Allowed</span>
                  </div>
                )}
                {listing?.utilitiesIncluded && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Utilities</span>
                    <span className="text-sm font-medium text-green-600">Included</span>
                  </div>
                )}
                {listing?.immediateAvailable && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Availability</span>
                    <span className="text-sm font-medium text-green-600">Immediate</span>
                  </div>
                )}
                {listing?.minimumLeaseMonths && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Min. Lease</span>
                    <span className="text-sm font-medium">{listing?.minimumLeaseMonths} months</span>
                  </div>
                )}
                {listing?.maximumLeaseMonths && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Max. Lease</span>
                    <span className="text-sm font-medium">{listing?.maximumLeaseMonths} months</span>
                  </div>
                )}
                {listing?.depositAmount && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Deposit</span>
                    <span className="text-sm font-medium">MVR {listing?.depositAmount?.toString()}</span>
                  </div>
                )}
                {listing?.maintenanceFee && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Maintenance Fee</span>
                    <span className="text-sm font-medium">MVR {listing?.maintenanceFeedepositAmount?.toString()}/month</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Views</span>
                  <span className="text-sm font-medium">{listing?.viewingCountdepositAmount?.toString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Saved</span>
                  <span className="text-sm font-medium">{listing?.favoriteCountdepositAmount?.toString()}</span>
                </div>
                {listing?.rating && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Rating</span>
                    <span className="flex items-center gap-1 text-sm font-medium">
                      <Star className="w-4 h-4 text-yellow-500 fill-current" />
                      {listing?.rating} ({listing?.reviews} reviews)
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Listing ID */}
            <div className="text-center text-xs text-gray-400 dark:text-gray-600">
              Listing ID: {listing?.product_id}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}